import { getRuleValue } from "../../data/ruleRegistry";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import { validateWallOpenings } from "../../domain/openings/validateOpeningSet";
import { ENGINE_PANEL_T_M, ENGINE_STUD_D_M } from "../../engines/constants";
import { resolveTwoRoomConfig } from "../../engines/twoRoomSauna";
import type { SaunaDimensions, TwoRoomConfig } from "../../engines/types";
import type { SaunaType } from "../../types/design";

export const DEFAULT_FRONT_DOOR_ID = "default-front-door";
export const DEFAULT_FRONT_DOOR_WIDTH_MM = 900;

// ── Heater-clearance constants ──────────────────────────────────────────────
// Mirror the one-room heater placement in heaterClearanceLayout.ts /
// calculateHeaterPlacement.ts: the heater always backs against the right (+X)
// wall, so its left edge in world-X is
//   innerWidth / 2 − heaterWidth − HEATER_WALL_MARGIN_M.
// We use the WIDEST heater (wood, 0.39m — see heaterGeometry WOOD_W) so the
// seeded door clears the heater regardless of which heater the user later picks.
// defaultOpeningsHeaterClearance.test.ts guards these against drift by checking
// the seeded door against the real heater box from deriveHeaterClearanceLayout.
const HEATER_WALL_MARGIN_M = 0.15;
const WIDEST_HEATER_WIDTH_M = 0.39;
const DOOR_HEATER_GAP_M = 0.05;
/**
 * Keep the seeded door at least this far from the front-wall corner. Mirrors
 * `framing.minCornerClearanceMm` (the same rule `validateOpeningSet` and
 * `OpeningDragController` enforce for exterior-wall corner-trim clearance)
 * so a freshly-reset design is always saveable — never below the live
 * minimum, regardless of admin overrides. On very narrow walls (<~1.8m)
 * this floor sits above the position that would fully clear the heater; see
 * the docstring below for why corner clearance wins that tradeoff.
 */
const FRONT_DOOR_MIN_CORNER_INSET_MM = getRuleValue<number>(
	"framing.minCornerClearanceMm",
);
export const DEFAULT_DIVIDER_DOOR_ID = "default-divider-door";
export const DEFAULT_DIVIDER_DOOR_WIDTH_MM = 660;
export const DEFAULT_DIVIDER_DOOR_HEIGHT_MM = 1860;
export const DEFAULT_RELAX_WINDOW_ID = "default-relax-window";
const DEFAULT_RELAX_WINDOW_WIDTH_MM = 800;
const DEFAULT_RELAX_WINDOW_HEIGHT_MM = 600;

export function getCenteredFrontDoorX(
	widthMm: number,
	doorWidthMm = DEFAULT_FRONT_DOOR_WIDTH_MM,
): number {
	return Math.round((widthMm - doorWidthMm) / 2);
}

/**
 * Default localX for the seeded front door.
 *
 * Models the one-room (fyrkantig) heater, which sits in the front-right corner, so
 * a *centered* door overlaps it on narrow walls (its right edge is always at
 * world-X +doorWidth/2, while the heater's left edge grows with width). To keep
 * the door clear of the heater — and out of the side-view sightline — we bias it
 * left just enough that its right edge clears the heater, then clamp to a sensible
 * corner inset. Wide walls (≳2.3m) keep the centered door unchanged.
 *
 * Pure function of width (no heater-type or sauna-type input), on purpose: the
 * store's resize-recenter path has neither, so reset and resize MUST share one
 * rule or the door jumps on first resize. It assumes the widest heater so it
 * clears every heater type. Applying the one-room rule to two-room seeds is
 * intentional and harmless — a narrow short-entry two-room also has a front-right
 * heater, and long-entry/panorama two-rooms relocate the entry off the front wall
 * anyway (twoRoomOpeningConfig.ts), overriding this seed.
 *
 * Corner clearance (`FRONT_DOOR_MIN_CORNER_INSET_MM`) always wins over heater
 * clearance when the two conflict. Below ~1.8m wide there isn't room to both
 * fully clear the heater *and* stay `framing.minCornerClearanceMm` away from
 * the corner — a hard `xMm < minCornerClearanceMm` violation blocks saving
 * the design entirely (`validateOpeningSet` gates `saveNow`/autosave), while
 * heater overlap is only an advisory 3D overlay warning
 * (`HeaterClearanceOverlay`), so we prioritize save-ability. See
 * `defaultOpeningsHeaterClearance.test.ts` (asserts full heater clearance
 * from 1.8m up) and docs/notes/2026-06-23-default-front-door-must-clear-the-front-right-heater.md.
 */
export function getDefaultFrontDoorX(
	widthMm: number,
	doorWidthMm = DEFAULT_FRONT_DOOR_WIDTH_MM,
): number {
	const centeredX = getCenteredFrontDoorX(widthMm, doorWidthMm);
	const widthM = widthMm / 1000;
	const innerWidthM = widthM - 2 * ENGINE_STUD_D_M - 2 * ENGINE_PANEL_T_M;
	const heaterLeftEdgeWorldX =
		innerWidthM / 2 - WIDEST_HEATER_WIDTH_M - HEATER_WALL_MARGIN_M;
	// Largest localX whose door right edge (world-X = −W/2 + (xMm+doorWidth)/1000)
	// still clears the heater's left edge by DOOR_HEATER_GAP_M.
	const maxClearX = Math.floor(
		(widthM / 2 + heaterLeftEdgeWorldX - DOOR_HEATER_GAP_M) * 1000 -
			doorWidthMm,
	);
	if (centeredX <= maxClearX) {
		return centeredX;
	}
	return Math.max(FRONT_DOOR_MIN_CORNER_INSET_MM, maxClearX);
}

export function getCenteredDividerDoorX(
	widthMm: number,
	doorWidthMm = DEFAULT_DIVIDER_DOOR_WIDTH_MM,
): number {
	return Math.round((widthMm - doorWidthMm) / 2);
}

/**
 * Compute the right-wall localX for the relax window, centered in the relaxation section.
 * On the right wall, localX runs along the Z axis (from back to front).
 * Default partition split = 0.5.
 */
export function getRelaxWindowX(
	lengthMm: number,
	windowWidthMm = DEFAULT_RELAX_WINDOW_WIDTH_MM,
	partitionSplit = 0.5,
): number {
	const relaxStartMm = lengthMm * partitionSplit; // divider position from back wall
	const relaxLengthMm = lengthMm * (1 - partitionSplit);
	const centerMm = relaxStartMm + relaxLengthMm / 2;
	return Math.round(centerMm - windowWidthMm / 2);
}

/** Y-offset for the relax window (centered vertically at H/2 + 0.2, where H = 2.1m). */
function getRelaxWindowY(wallHeightMm = 2100): number {
	const centerY = wallHeightMm / 2 + 200; // matches engine's ctx.H / 2 + 0.2
	return Math.round(centerY - DEFAULT_RELAX_WINDOW_HEIGHT_MM / 2);
}

/**
 * Whether a relax window of `windowWidthMm`, centered per `getRelaxWindowX`,
 * actually fits on the right wall without violating the same corner-clearance
 * rule `validateOpeningSet` enforces at save time. Reuses `validateWallOpenings`
 * directly (rather than re-deriving the width+2×clearance inequality here) so
 * this can never drift from what the save-time gate actually checks — see
 * `getDefaultFrontDoorX` above for the sibling pattern on the front door.
 *
 * On short two-room walls (below ~2.2m at the default 800mm width / 0.5
 * split) the centered window has no valid position at all: it either starts
 * before the corner-clearance floor or its far edge runs past the opposite
 * corner. There's no "push it over" fallback like the front door has (no
 * heater to bias away from) — the caller should simply omit the window.
 */
function relaxWindowFits(
	lengthMm: number,
	windowWidthMm: number,
	windowHeightMm: number,
	xMm: number,
	yMm: number,
): boolean {
	const issues = validateWallOpenings({
		openings: [
			{
				id: DEFAULT_RELAX_WINDOW_ID,
				wall: "right",
				type: "window",
				xMm,
				yMm,
				widthMm: windowWidthMm,
				heightMm: windowHeightMm,
				glazed: true,
			},
		],
		wallLengthMm: lengthMm,
	});
	return issues.length === 0;
}

interface DefaultOpeningsOptions {
	partitionSplit?: number;
	twoRoomConfig?: Partial<TwoRoomConfig>;
}

export function getDefaultOpenings(
	type: SaunaType,
	dimensions: SaunaDimensions,
	options: DefaultOpeningsOptions = {},
): WallOpening[] {
	if (type === "tunna") {
		return [];
	}

	const widthMm = Math.round(dimensions.width * 1000);

	const openings: WallOpening[] = [
		{
			id: DEFAULT_FRONT_DOOR_ID,
			wall: "front",
			type: "door",
			// Applied to every type on purpose — see getDefaultFrontDoorX (the rule is
			// width-only so seed and resize agree; harmless for two-room).
			xMm: getDefaultFrontDoorX(widthMm),
			yMm: 0,
			widthMm: DEFAULT_FRONT_DOOR_WIDTH_MM,
			heightMm: 1900,
			glazed: true,
		},
	];

	const twoRoomConfig =
		type === "tvarum" ? resolveTwoRoomConfig(options.twoRoomConfig) : null;

	if (type === "tvarum" && twoRoomConfig?.hasRelaxRoom) {
		// (The relax window below is dropped for panorama glazing, which
		// replaces the whole right wall.)
		openings.push({
			id: DEFAULT_DIVIDER_DOOR_ID,
			wall: "divider",
			type: "door",
			xMm: getCenteredDividerDoorX(widthMm),
			yMm: 0,
			widthMm: DEFAULT_DIVIDER_DOOR_WIDTH_MM,
			heightMm: DEFAULT_DIVIDER_DOOR_HEIGHT_MM,
			glazed: true,
		});

		const lengthMm = Math.round(dimensions.length * 1000);
		const relaxWindowXMm = getRelaxWindowX(
			lengthMm,
			DEFAULT_RELAX_WINDOW_WIDTH_MM,
			options.partitionSplit,
		);
		const relaxWindowYMm = getRelaxWindowY();
		// Don't seed a relax window the wall is too short to hold — see
		// relaxWindowFits above. Below the fit threshold, the room simply
		// starts with no relax-room window; the user can add one manually and
		// OpeningsEditor's own validation guides placement from there.
		if (
			relaxWindowFits(
				lengthMm,
				DEFAULT_RELAX_WINDOW_WIDTH_MM,
				DEFAULT_RELAX_WINDOW_HEIGHT_MM,
				relaxWindowXMm,
				relaxWindowYMm,
			) &&
			twoRoomConfig.glazingStyle !== "panorama"
		) {
			openings.push({
				id: DEFAULT_RELAX_WINDOW_ID,
				wall: "right",
				type: "window",
				xMm: relaxWindowXMm,
				yMm: relaxWindowYMm,
				widthMm: DEFAULT_RELAX_WINDOW_WIDTH_MM,
				heightMm: DEFAULT_RELAX_WINDOW_HEIGHT_MM,
				glazed: true,
			});
		}
	}

	return openings;
}
